import { useLocation } from "wouter";
import { ArrowLeftRight, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";

export default function ImpersonationBanner() {
  const [, setLocation] = useLocation();
  const {
    isImpersonating,
    user,
    staffUser,
    exitImpersonationMutation,
    logoutMutation,
  } = useAuth();

  if (!isImpersonating) return null;

  const handleSwitch = () => {
    exitImpersonationMutation.mutate(undefined, {
      onSuccess: () => setLocation("/select-sidekick"),
    });
  };

  const firstName = (user?.name ?? user?.email ?? "sidekick").split(" ")[0];
  const staffName = (staffUser?.name ?? staffUser?.email ?? "you").split(" ")[0];

  return (
    <div className="sticky top-0 z-40 border-b border-amber-300/50 bg-amber-50/90 backdrop-blur-sm text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/[0.18] dark:text-amber-100">
      <div className="px-4 sm:px-5 h-9 lg:h-[38px] flex items-center justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <ShieldCheck className="h-3.5 w-3.5 lg:h-4 lg:w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-xs lg:text-[13px] truncate">
            <span className="font-semibold">
              Acting as <span className="font-bold">{firstName}</span>
            </span>
            <span className="text-amber-700/80 dark:text-amber-200/70">
              {" "}· staff: {staffName}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Desktop: labelled buttons */}
          <Button
            variant="ghost"
            size="sm"
            className="hidden lg:inline-flex h-7 rounded-[7px] px-2.5 text-[13px] text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:text-amber-100 dark:hover:bg-amber-500/20"
            onClick={handleSwitch}
            disabled={exitImpersonationMutation.isPending}
          >
            <ArrowLeftRight className="h-3.5 w-3.5 mr-1.5" />
            Switch sidekick
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="hidden lg:inline-flex h-7 rounded-[7px] px-2.5 text-[13px] text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:text-amber-100 dark:hover:bg-amber-500/20"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            <LogOut className="h-3.5 w-3.5 mr-1.5" />
            Exit
          </Button>
          {/* Mobile: icon-only buttons */}
          <button
            type="button"
            title="Switch sidekick"
            className="lg:hidden flex h-[30px] w-[30px] items-center justify-center rounded-lg text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-300 dark:hover:bg-amber-500/20"
            onClick={handleSwitch}
            disabled={exitImpersonationMutation.isPending}
          >
            <ArrowLeftRight className="h-[15px] w-[15px]" />
            <span className="sr-only">Switch sidekick</span>
          </button>
          <button
            type="button"
            title="Exit"
            className="lg:hidden flex h-[30px] w-[30px] items-center justify-center rounded-lg text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-300 dark:hover:bg-amber-500/20"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            <LogOut className="h-[15px] w-[15px]" />
            <span className="sr-only">Exit</span>
          </button>
        </div>
      </div>
    </div>
  );
}
